import { useLayoutEffect, useState } from "react";
import { Menu } from "@mui/material";
import type { PopoverOrigin } from "@mui/material/Popover";
import MenuPanel from "./MenuPanel";
import { MenuItem } from "./MenuRow";

type Placement = "top-start" | "right-start";

interface MenuPanelPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  items: MenuItem[];
  width?: number | "hug";
  placement?: Placement;
  className?: string;
  paperSx?: Record<string, string | number>;
}

const ROW_HEIGHT = 36;
const PANEL_PADDING_Y = 32;
const VIEWPORT_MARGIN = 16;

function getOrigins(placement: Placement, flipUp: boolean): {
  anchorOrigin: PopoverOrigin;
  transformOrigin: PopoverOrigin;
} {
  if (placement === "top-start" || flipUp) {
    return {
      anchorOrigin: { vertical: "top", horizontal: "right" },
      transformOrigin: { vertical: "bottom", horizontal: "left" },
    };
  }
  return {
    anchorOrigin: { vertical: "bottom", horizontal: "right" },
    transformOrigin: { vertical: "top", horizontal: "left" },
  };
}

export default function MenuPanelPopover({
  anchorEl,
  open,
  onClose,
  items,
  width,
  placement = "right-start",
  className,
  paperSx,
}: MenuPanelPopoverProps): JSX.Element {
  const [flipUp, setFlipUp] = useState(false);

  useLayoutEffect(() => {
    if (!open || !anchorEl || placement !== "right-start") {
      setFlipUp(false);
      return;
    }
    const rect = anchorEl.getBoundingClientRect();
    const estimatedHeight = items.length * ROW_HEIGHT + PANEL_PADDING_Y;
    setFlipUp(window.innerHeight - rect.bottom < estimatedHeight + VIEWPORT_MARGIN);
  }, [open, anchorEl, placement, items.length]);

  const { anchorOrigin, transformOrigin } = getOrigins(placement, flipUp);

  return (
    <Menu
      className={className}
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      onClick={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: "visible",
          background: "transparent",
          boxShadow: "none",
          "& .MuiList-root": { padding: 0 },
          ...(paperSx ?? {}),
        },
      }}
    >
      <MenuPanel items={items} width={width} />
    </Menu>
  );
}
